import React from 'react';
import { useTheme } from '../../context/ThemeContext';

export default function AuthSkeleton({ isRegister = false }) {
  const { isDark } = useTheme();

  const cardBg = isDark ? '#161C1A' : '#FFFFFF';
  const cardBorder = isDark ? '#2B3531' : '#E1E7E4';
  const pageBg = isDark ? '#0E1211' : '#F7F7F4';
  const shimmerBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const inputBg = isDark ? '#111614' : '#F8FAFC';

  return (
    <div 
      className="flex min-h-screen w-full overflow-hidden antialiased transition-colors duration-300"
      style={{ backgroundColor: pageBg }}
    >
      {/* Left Panel Skeleton (Desktop) */}
      <div className="relative hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col justify-between p-12 xl:p-16 bg-gradient-to-br from-[#176B5B] via-[#125447] to-[#0A2E26] dark:from-[#0c1412] dark:via-[#111c19] dark:to-[#080d0c] border-r border-[#176B5B]/20 dark:border-[#2B3531]">
        {/* Brand Header Skeleton */}
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 rounded-2xl bg-white/15 animate-pulse" />
          <div className="space-y-2">
            <div className="w-28 h-5 rounded-md bg-white/20 animate-pulse" />
            <div className="w-36 h-3 rounded-md bg-white/15 animate-pulse" />
          </div>
        </div>

        {/* Hero Copy Skeleton */}
        <div className="my-auto py-8 space-y-6 max-w-md">
          <div className="w-48 h-6 rounded-full bg-white/15 animate-pulse" />
          <div className="space-y-3">
            <div className="w-full h-10 rounded-xl bg-white/20 animate-pulse" />
            <div className="w-4/5 h-10 rounded-xl bg-white/20 animate-pulse" />
          </div>
          <div className="w-full h-16 rounded-xl bg-white/15 animate-pulse" />
          
          <div className="space-y-3 pt-4">
            <div className="w-full h-14 rounded-2xl bg-white/10 animate-pulse" />
            <div className="w-full h-14 rounded-2xl bg-white/10 animate-pulse" />
            <div className="w-full h-14 rounded-2xl bg-white/10 animate-pulse" />
          </div>
        </div>

        {/* Footer Skeleton */}
        <div className="flex justify-between items-center pt-6 border-t border-white/10">
          <div className="w-36 h-3 rounded-md bg-white/15 animate-pulse" />
          <div className="w-28 h-3 rounded-md bg-white/15 animate-pulse" />
        </div>
      </div>

      {/* Right Panel Skeleton - Auth Form Card */}
      <div className="relative flex-1 flex flex-col justify-between items-center p-6 sm:p-10 lg:p-12 min-h-screen">
        {/* Top bar */}
        <div className="w-full flex items-center justify-between mb-6">
          <div className="w-24 h-8 rounded-xl animate-pulse" style={{ backgroundColor: shimmerBg }} />
          <div className="flex gap-2">
            <div className="w-9 h-9 rounded-xl animate-pulse" style={{ backgroundColor: shimmerBg }} />
            <div className="w-24 h-9 rounded-xl animate-pulse" style={{ backgroundColor: shimmerBg }} />
          </div>
        </div>

        {/* Center Card */}
        <div className={`w-full ${isRegister ? 'max-w-[480px]' : 'max-w-[440px]'} my-auto`}>
          <div 
            className="border shadow-[0_20px_50px_rgba(0,0,0,0.06)] rounded-[2rem] p-6 sm:p-10 space-y-6"
            style={{ backgroundColor: cardBg, borderColor: cardBorder }}
          >
            {/* Header */}
            <div className="space-y-3 text-center sm:text-left">
              <div className="w-28 h-5 rounded-full bg-emerald-500/20 animate-pulse mx-auto sm:mx-0" />
              <div className="w-48 h-8 rounded-xl animate-pulse mx-auto sm:mx-0" style={{ backgroundColor: shimmerBg }} />
              <div className="w-64 h-4 rounded-md animate-pulse mx-auto sm:mx-0" style={{ backgroundColor: shimmerBg }} />
            </div>

            {/* Form Fields Skeleton */}
            <div className="space-y-4 pt-2">
              {isRegister && (
                <div className="space-y-2">
                  <div className="w-20 h-3 rounded animate-pulse" style={{ backgroundColor: shimmerBg }} />
                  <div className="w-full h-12 rounded-2xl border animate-pulse" style={{ backgroundColor: inputBg, borderColor: cardBorder }} />
                </div>
              )}

              <div className="space-y-2">
                <div className="w-24 h-3 rounded animate-pulse" style={{ backgroundColor: shimmerBg }} />
                <div className="w-full h-12 rounded-2xl border animate-pulse" style={{ backgroundColor: inputBg, borderColor: cardBorder }} />
              </div>

              <div className="space-y-2">
                <div className="w-20 h-3 rounded animate-pulse" style={{ backgroundColor: shimmerBg }} />
                <div className="w-full h-12 rounded-2xl border animate-pulse" style={{ backgroundColor: inputBg, borderColor: cardBorder }} />
              </div>

              {isRegister && (
                <div className="space-y-2">
                  <div className="w-28 h-3 rounded animate-pulse" style={{ backgroundColor: shimmerBg }} />
                  <div className="w-full h-12 rounded-2xl border animate-pulse" style={{ backgroundColor: inputBg, borderColor: cardBorder }} />
                </div>
              )}

              {/* Remember Me / Forgot */}
              <div className="flex justify-between items-center pt-1">
                <div className="w-32 h-4 rounded animate-pulse" style={{ backgroundColor: shimmerBg }} />
                <div className="w-24 h-4 rounded animate-pulse" style={{ backgroundColor: shimmerBg }} />
              </div>

              {/* Submit Button Skeleton */}
              <div className="pt-2">
                <div className="w-full h-12 rounded-2xl bg-gradient-to-r from-[#176B5B] to-[#125447] opacity-60 animate-pulse" />
              </div>

              {/* Footer Switch Link Skeleton */}
              <div className="w-48 h-4 rounded animate-pulse mx-auto pt-2" style={{ backgroundColor: shimmerBg }} />
            </div>
          </div>
        </div>

        <div className="w-full text-center py-2 lg:hidden">
          <div className="w-40 h-3 rounded animate-pulse mx-auto" style={{ backgroundColor: shimmerBg }} />
        </div>
      </div>
    </div>
  );
}
